import type { StaffAccountDto, StaffDirectoryDto } from "@shoppy/shared";
import { useEffect, useState } from "react";
import { call } from "./api";
import type { ApiResult, Failure } from "./api-result";

/**
 * The staff directory, read once per session and shared by everything that
 * draws a colleague's name.
 *
 * ── Why this is cached and `useRead` is not enough ──────────────────────────
 * Three surfaces want it at once — the support queue's «Assigned to» column,
 * its assign dialog, and the ticket drill panel opened over the top of both —
 * and `useRead` fires a request per mount. Double-clicking five tickets in a
 * row would be five reads of a list that has not changed, and the panel's would
 * land after the row it is drawn beside had already been named.
 *
 * **What the cache does NOT do is invalidate.** The directory changes when
 * somebody gains or loses a staff role, and there is no screen in this console
 * that does either — `admin.accounts.manage` has no surface here. So «until the
 * tab is reloaded» is the right lifetime, and a colleague who joined this
 * morning appearing after a refresh is the honest consequence. If an account
 * register is ever built, it calls `forgetStaffDirectory` after a write.
 *
 * A failure is cached too, briefly — as a settled answer rather than as a
 * retry loop. A directory the operator may not read (`admin.accounts.read` is
 * on ADMIN and FINANCE, so this should not happen) or an api that is down must
 * not turn a register into one request per mount forever.
 */

export interface StaffDirectory {
  /** `null` while the answer has not arrived, or after one that failed. Never an empty list standing for either. */
  readonly staff: readonly StaffAccountDto[] | null;
  /** The api's own cap was hit: the list is short and the console must say so rather than look complete. */
  readonly truncated: boolean;
  readonly failure: Failure | null;
  readonly loading: boolean;
}

const EMPTY: StaffDirectory = { staff: null, truncated: false, failure: null, loading: true };

let cache: Promise<ApiResult<StaffDirectoryDto>> | null = null;
const listeners = new Set<() => void>();

function load(): Promise<ApiResult<StaffDirectoryDto>> {
  // No `signal`: the read is shared, so one component unmounting must not abort
  // the answer another is waiting for. It is a bounded list behind the console's
  // own front door, and abandoning it costs one small response.
  cache ??= call("GET /v1/admin/accounts", {});
  return cache;
}

/** Drops the cached answer. For an account register's writes; nothing calls it today. */
export function forgetStaffDirectory(): void {
  cache = null;
  for (const notify of listeners) notify();
}

export function useStaffDirectory(): StaffDirectory {
  const [state, setState] = useState<StaffDirectory>(EMPTY);

  useEffect(() => {
    let live = true;
    const run = () => {
      setState(EMPTY);
      void load().then((result) => {
        if (!live) return;
        setState(
          result.ok
            ? { staff: result.data.items, truncated: result.data.truncated, failure: null, loading: false }
            : { staff: null, truncated: false, failure: result, loading: false },
        );
      });
    };
    run();
    listeners.add(run);
    return () => {
      live = false;
      listeners.delete(run);
    };
  }, []);

  return state;
}
